import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const OAuthCallbackSimple: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log('OAuth callback processing...');

        const token = searchParams.get('token');
        const refreshToken = searchParams.get('refreshToken');
        const error = searchParams.get('error');

        if (error) {
          console.error('OAuth error:', error);
          setStatus('error');
          setErrorMessage(decodeURIComponent(error));
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          return;
        }

        if (token && refreshToken) {
          console.log('Tokens received, storing...');
          console.log('[OAuth] Received tokens:', { 
            tokenLength: token.length, 
            refreshTokenLength: refreshToken.length 
          });
          
          try {
            // Clear any existing tokens first
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            
            // Store new tokens
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', refreshToken);
            
            // Verify tokens were stored (mobile Safari can be strict)
            const storedToken = localStorage.getItem('token');
            const storedRefreshToken = localStorage.getItem('refreshToken');
            
            if (!storedToken || !storedRefreshToken) {
              console.error('[OAuth] Failed to store tokens in localStorage');
              setStatus('error');
              setErrorMessage('Failed to save login credentials. Please try again.');
              setTimeout(() => {
                window.location.href = '/login';
              }, 3000);
              return;
            }
            
            console.log('[OAuth] Tokens stored successfully');
            console.log('Tokens stored successfully:', {
              tokenStored: !!storedToken,
              refreshTokenStored: !!storedRefreshToken
            });
            
            setStatus('success');
            
            // Add a small delay to ensure localStorage is written
            setTimeout(() => {
              // Use location.replace to avoid back button issues on mobile
              console.log('[OAuth] Redirecting to dashboard...');
              window.location.replace('/dashboard');
            }, 500);
          } catch (storageError) {
            console.error('Storage error:', storageError);
            setStatus('error');
            setErrorMessage('Failed to store login tokens. Please try again.');
            setTimeout(() => {
              window.location.href = '/login';
            }, 3000);
          }
        } else {
          console.error('Missing tokens:', { token: !!token, refreshToken: !!refreshToken });
          setStatus('error');
          setErrorMessage('Invalid login response - missing tokens');
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setErrorMessage('An error occurred during login');
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    };

    // Add a small delay to ensure all URL parameters are parsed
    const timeoutId = setTimeout(handleOAuthCallback, 100);
    
    return () => clearTimeout(timeoutId);
  }, [searchParams]);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
      padding: '20px',
      textAlign: 'center'
    }}>
      {status === 'processing' && (
        <>
          <h2>Completing sign in...</h2>
          <div style={{ marginTop: '20px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
          <p>Please wait while we process your authentication...</p>
        </>
      )}
      
      {status === 'success' && (
        <>
          <h2 style={{ color: '#4caf50' }}>✓ Login successful!</h2>
          <p>Redirecting to dashboard...</p>
        </>
      )}
      
      {status === 'error' && (
        <>
          <h2 style={{ color: '#f44336' }}>Login failed</h2>
          <p>{errorMessage}</p>
          <p style={{ marginTop: '20px' }}>Redirecting to login page...</p>
        </>
      )}
      
      <p style={{ marginTop: '40px', fontSize: '14px', color: '#666' }}>
        If you're not redirected automatically, <a href="/dashboard">click here</a>
      </p>
    </div>
  );
};

export default OAuthCallbackSimple;